import React, { useState, useCallback, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";

// Utility function to compare object equality
import areObjectsEqual from "../../../../utils/areObjectsEqual";
import { sanitizeFileName } from "../../../../utils";
import Spinner from "../../../Template/Components/Spinner";
import { maxFileSizeValidation } from "../../../../utils/common";

// Constants
const MAX_ORDER_LENGTH = 4;
const MIN_ORDER_VALUE = 1;

// Initial error state
const INITIAL_ERRORS = {
  image: false,
  order: false,
  orderExists: false,
};

/**
 * Form component for creating/editing layer choices
 */
const Form = ({ handleSubmit, formData, onDiscard, typeCheck, layerId }) => {
  const [form, setForm] = useState(formData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  /**
   * Check if there are unsaved changes
   */
  const changesFound = useMemo(
    () => !areObjectsEqual({ ...formData }, form),
    [formData, form]
  );

  /**
   * Check if the order exists for this layer choice
   */
  const checkOrderUniqueness = useCallback(async (order, layerId, id) => {
    try {
      const res = await fetch(`/api/layer/layerchoice/checkorder/${order}/${layerId}/${id}`);
      if (res.status === 200) {
        const data = await res.json();
        return data.checkStatus || false;
      }
      return false;
    } catch (error) {
      console.error("Error checking order uniqueness:", error);
      return false;
    }
  }, []);

  /**
   * Upload image to server
   */
  const uploadImage = useCallback(async (file, encryptedName) => {
    try {
      const formData = new FormData();
      formData.append("file", file, encryptedName);
      return axios.post(`/api/file/upload`, formData);
    } catch (error) {
      console.error("Error uploading image:", error);
      throw error;
    }
  }, []);

  /**
   * Validate form data
   */
  const validateForm = useCallback(async () => {
    const newErrors = { ...INITIAL_ERRORS };

    // Validate image
    if (!form.layer_preview_url) {
      newErrors.image = true;
    }

    // Validate order
    if (!form.order || form.order < MIN_ORDER_VALUE) {
      newErrors.order = true;
    }

    // Check order uniqueness
    if (form.order && form.order >= MIN_ORDER_VALUE) {
      const checkId = typeCheck === "createItem" ? 0 : formData.id;
      const isOrderExists = await checkOrderUniqueness(form.order, layerId, checkId);
      newErrors.orderExists = isOrderExists;
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error === true);
  }, [form, typeCheck, formData.id, layerId, checkOrderUniqueness]);

  /**
   * Handle changes in input fields
   */
  const handleChange = useCallback((e) => {
    let value = e.target.value;

    // Ensure the order value is numeric and limited
    if (e.target.name === "order") {
      value = value.slice(0, MAX_ORDER_LENGTH);
    }

    setForm((prev) => ({
      ...prev,
      [e.target.name]: value,
    }));

    // Reset errors when user makes changes
    setErrors(INITIAL_ERRORS);
  }, []);

  /**
   * Prevent negative sign input for number fields
   */
  const handleKeyDown = useCallback((e) => {
    if (e.key === "-") {
      e.preventDefault();
    }
  }, []);

  /**
   * Handle click event for the save button
   */
  const handleClick = useCallback(async () => {
    setLoading(true);

    try {
      // Upload image if there's a new file
      if (form.uploadedfile) {
        await uploadImage(form.uploadedfile, form.layer_preview_url);
      }

      // Validate form
      const isValid = await validateForm();

      // If validation passes, submit the form
      if (isValid) {
        handleSubmit(form);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  }, [form, uploadImage, validateForm, handleSubmit]);

  /**
   * Handle file drop in dropzone
   */
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setForm((prev) => ({
        ...prev,
        uploadedfile: file,
        layer_preview_url: sanitizeFileName(file?.name),
        layer_preview_url_preview: URL.createObjectURL(file),
      }));
      setErrors(INITIAL_ERRORS);
    }
  }, []);

  /**
   * Handle image change from file input
   */
  const onImageChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    const error = maxFileSizeValidation(selectedFile);
    
    if (selectedFile && !error) {
      setForm((prev) => ({
        ...prev,
        uploadedfile: selectedFile,
        layer_preview_url: sanitizeFileName(selectedFile?.name),
        layer_preview_url_preview: URL.createObjectURL(selectedFile),
      }));
      setErrors(INITIAL_ERRORS);
    } else if (error) {
      setErrors((prev) => ({ ...prev, image: error }));
    }
  }, []);

  /**
   * Configure dropzone
   */
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  /**
   * Get error message for image validation
   */
  const imageErrorMessage = useMemo(() => {
    if (!errors.image) return null;
    return typeof errors.image === "string" ? errors.image : "Layer Choice is required";
  }, [errors.image]);

  return (
    <>
      {/* Image Upload Section */}
      <div>
        <div
          className="mt-3 mb-5 upload-image-group"
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto 1fr",
            gridTemplateRows: "auto auto",
            alignItems: "center",
            gap: "1rem",
            width: "100%",
          }}
        >
          {/* Upload Button */}
          <div>
            <label htmlFor="ct_icon_nft" className="btn btn-upload form-btn">
              Upload Image
              <input
                type="file"
                id="ct_icon_nft"
                name="ct_icon_nft"
                accept="image/*"
                onChange={onImageChange}
              />
            </label>
          </div>

          {/* OR Separator */}
          <span style={{ fontSize: "1.5rem", alignSelf: "center" }}>or</span>

          {/* Dropzone */}
          <div style={{ width: "100%" }}>
            <div
              {...getRootProps()}
              style={{
                border: form.layer_preview_url_preview
                  ? "1px solid #70707099"
                  : "1px dashed #70707099",
                padding: form.layer_preview_url_preview ? "2rem" : "4rem 2rem",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#ededed",
                flexGrow: form.layer_preview_url_preview ? "0" : "1",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <input {...getInputProps()} />
              {form.layer_preview_url_preview ? (
                <img
                  src={form.layer_preview_url_preview}
                  className="custom-choose-file"
                  alt="Preview of the uploaded thumbnail"
                />
              ) : (
                <p style={{ fontSize: "1.5rem", color: "#707070" }}>
                  Drag and drop layer choice here
                </p>
              )}
            </div>
          </div>

          {/* Error Message */}
          <div
            style={{
              gridColumn: "3",
              gridRow: "2",
              width: "100%",
              textAlign: "right",
            }}
          >
            {imageErrorMessage && (
              <span
                id="errorLayerImage"
                className="form-text text-danger"
                style={{ display: "block" }}
              >
                {imageErrorMessage}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Order Input */}
      <div className="mt-5">
        <label htmlFor="order" className="form-labels">
          Order
        </label>
        <input
          type="number"
          name="order"
          id="order"
          min={MIN_ORDER_VALUE}
          placeholder=""
          onChange={handleChange}
          value={form.order || ""}
          className="form-control inputs"
          maxLength={MAX_ORDER_LENGTH}
          onKeyDown={handleKeyDown}
        />
        {errors.order && (
          <span className="form-text text-danger" style={{ display: "block" }}>
            Order is required and must be greater than 0
          </span>
        )}
        {errors.orderExists && (
          <span className="form-text text-danger" style={{ display: "block" }}>
            This order is already assigned. Please select a different one
          </span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="pt-5">
        <button
          type="button"
          className="btn btn-secondary form-btn me-4"
          onClick={onDiscard}
          aria-label="Discard changes"
        >
          Discard
        </button>
        <button
          type="button"
          className="btn bg-orange form-btn"
          onClick={handleClick}
          disabled={!changesFound}
          aria-label="Save changes"
        >
          Save Changes
        </button>
      </div>

      {loading && <Spinner spinner={loading} />}
    </>
  );
};

export default Form;
